/**
 * iter-386u — Public Project Status page.
 *
 * Renders a shareable, no-login view of a project's payment/ledger status
 * for a client. Data fetched via GET /api/public/project-status/{token}.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { IndianRupee, Calendar, CheckCircle2, TrendingUp, Wallet, AlertCircle } from 'lucide-react';

function INR(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

export default function PublicProjectStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/project-status/${token}`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Link is invalid or expired');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <p className="text-stone-500">Loading project status...</p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="max-w-md text-center space-y-3">
          <AlertCircle size={40} className="mx-auto text-red-400" />
          <p className="text-lg font-semibold text-stone-900">Link unavailable</p>
          <p className="text-stone-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }

  const { project, financial_summary: fin, progress, recent_transactions: txns, organization: org, link } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100" data-testid="public-project-status">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">{org.name}</p>
              <h1 className="font-manrope text-3xl font-bold text-stone-900">{project.name}</h1>
              <p className="text-stone-600 mt-1">Client: <span className="font-semibold">{project.client_name || '—'}</span></p>
            </div>
            <div className="text-right text-xs text-stone-500">
              <div className="flex items-center gap-1 justify-end">
                <Calendar size={12} /> {project.start_date || '—'} → {project.end_date || '—'}
              </div>
              {link?.expiry_date && (
                <p className="mt-1">Link expires {link.expiry_date.slice(0, 10)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Financial cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 border border-stone-200" data-testid="stat-contract">
            <div className="flex items-center gap-1.5 text-xs text-stone-500 mb-1"><TrendingUp size={12} /> Contract Value</div>
            <p className="text-xl font-bold text-stone-900">{INR(fin.contract_value)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-emerald-200" data-testid="stat-received">
            <div className="flex items-center gap-1.5 text-xs text-emerald-700 mb-1"><IndianRupee size={12} /> Total Received</div>
            <p className="text-xl font-bold text-emerald-700">{INR(fin.total_received)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-amber-200" data-testid="stat-balance">
            <div className="flex items-center gap-1.5 text-xs text-amber-700 mb-1"><Wallet size={12} /> Balance Due</div>
            <p className="text-xl font-bold text-amber-700">{INR(fin.balance_due)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-200" data-testid="stat-progress">
            <div className="flex items-center gap-1.5 text-xs text-blue-700 mb-1"><CheckCircle2 size={12} /> Progress</div>
            <p className="text-xl font-bold text-blue-700">{progress.percentage}%</p>
            <p className="text-[10px] text-stone-500">{progress.completed} / {progress.total_tasks} tasks</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-stone-900">Payment progress</p>
            <p className="text-xs text-stone-500">{Math.round((fin.total_received / Math.max(1, fin.contract_value)) * 100)}%</p>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
              style={{ width: `${Math.min(100, Math.round((fin.total_received / Math.max(1, fin.contract_value)) * 100))}%` }}
              data-testid="payment-progress-bar"
            />
          </div>
        </div>

        {/* Recent transactions */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <p className="text-sm font-semibold text-stone-900 mb-3">Recent transactions ({txns.length})</p>
          {txns.length === 0 ? (
            <p className="text-sm text-stone-500">No transactions recorded yet.</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {txns.map((t, i) => (
                <div key={i} className="py-2.5 flex items-center justify-between gap-3" data-testid={`public-txn-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {t.direction === 'received' ? (
                      <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold">IN</span>
                    ) : (
                      <span className="shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px] font-bold">OUT</span>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm text-stone-900 truncate">
                        {t.direction === 'received' ? 'Received' : 'Paid'}{t.party ? ` · ${t.party}` : ''}
                      </div>
                      <div className="text-[10px] text-stone-500 truncate">
                        {t.payment_mode || ''}{t.reference ? ` · ${t.reference}` : ''}{t.date ? ` · ${String(t.date).slice(0, 10)}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className={`text-sm font-bold whitespace-nowrap ${t.direction === 'received' ? 'text-emerald-700' : 'text-red-700'}`}>
                    {t.direction === 'received' ? '+' : '-'}{INR(t.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-stone-400 py-4">
          Shared securely by {org.name}. This is a read-only view.
        </p>
      </div>
    </div>
  );
}
